// ============================================================
// App.tsx - 메인 라우팅
// ============================================================

import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ErrorBoundary from './components/Layout/ErrorBoundary';
import { clearStaleChunkMark, reloadIfStaleChunk } from './utils/staleChunk';

// 재배포로 청크가 사라진 낡은 탭은 페이지 자체를 못 불러온다. 그 경우 한 번
// 새로고침해 스스로 낫게 하고(새로고침 중에는 영영 resolve되지 않는 약속을
// 돌려 로더를 그대로 둔다), 진짜 실패는 ErrorBoundary로 올려보낸다.
const lazyPage = (load: () => Promise<{ default: React.ComponentType }>) =>
  React.lazy(() =>
    load().then(
      (mod) => {
        clearStaleChunkMark();
        return mod;
      },
      (err: unknown) => {
        if (reloadIfStaleChunk(err)) return new Promise<never>(() => {});
        throw err;
      },
    ),
  );

const WorkbenchPage = lazyPage(() => import('./pages/workbench/WorkbenchPage'));
const ChatbotPage = lazyPage(() => import('./pages/ChatbotPage'));

const PageLoader: React.FC = () => (
  <div className="flex min-h-screen items-center justify-center bg-[#f7f7f4]">
    <div className="flex flex-col items-center gap-3 text-slate-400">
      <svg
        className="h-8 w-8 animate-spin text-teal-500"
        viewBox="0 0 24 24"
        fill="none"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="3"
          strokeDasharray="40"
          strokeDashoffset="10"
          strokeLinecap="round"
        />
      </svg>
      <p className="text-sm font-bold">innohomes 화면 로딩 중...</p>
    </div>
  </div>
);

const App: React.FC = () => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<WorkbenchPage />} />
        <Route path="/chatbot" element={<ChatbotPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  </ErrorBoundary>
);

export default App;
